/**
 * CompactMetricStrip — secondary metrics row, single line, no borders.
 *
 * V2.6 Worker-B (2026-04-20). After the HeadlineHero + 3 StoryCards carry
 * the narrative, secondary metrics (Ride Gross, Guests, Riders/hr,
 * Orders/hr) live here as a flat strip so they stay accessible without
 * competing with the headline for attention.
 *
 * Caller controls formatting + ordering. Labels render upper-case micro
 * with ink-soft color; values render as Butler display type with
 * tabular-nums so the row aligns numerically. Items may carry a YoY
 * percent — the strip threads it through the shared YoYChip so YoY
 * signal stays consistent with the hero.
 */

import { YoYChip } from '../analytics/YoYChip'

export interface CompactMetricStripItem {
  key: string
  label: string
  value: string
  /** Optional YoY %. `null` or missing → no chip. */
  yoyPct?: number | null
  /**
   * Test ID for the item container. A sibling `<chip>-yoy` testid is
   * attached to the YoY chip automatically when yoyPct is present.
   */
  'data-testid'?: string
}

export interface CompactMetricStripProps {
  items: CompactMetricStripItem[]
  'data-testid'?: string
}

export function CompactMetricStrip({
  items,
  'data-testid': testId = 'dashboard-metric-strip',
}: CompactMetricStripProps) {
  if (!Array.isArray(items) || items.length === 0) return null
  return (
    <div
      data-testid={testId}
      className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-2 border-t border-hairline pt-3 dark:border-slate-700"
    >
      {items.map((item) => {
        const itemTestId = item['data-testid'] ?? `${testId}-${item.key}`
        return (
          <div
            key={item.key}
            data-testid={itemTestId}
            className="flex items-baseline gap-2"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-soft dark:text-slate-400">
              {item.label}
            </span>
            <span className="font-display text-base font-semibold text-butler-800 tabular-nums dark:text-butler-100">
              {item.value}
            </span>
            <YoYChip
              pct={item.yoyPct ?? null}
              data-testid={`${itemTestId}-yoy`}
            />
          </div>
        )
      })}
    </div>
  )
}
